import { Popconfirm } from "antd";
import React, { useCallback, useMemo, useState, useTransition } from "react";
import { graphql, useLazyLoadQuery, useMutation, usePaginationFragment } from "react-relay/hooks";
import { useParams } from "react-router";

import { Button, Input, useDebouncedState } from "@skydio/rivet-ui";

import Breadcrumbs from "components/common/Breadcrumbs";
import { legacyToRivetPagination, useRelayPagination } from "components/common/Pagination";
import PaginatedTable from "components/common/table/PaginatedTable";
import SettingsPageView from "components/main/settings/SettingsPageView";
import { useOrgPrefixedPath } from "hooks/orgPaths";

import AddUserToGroupModal from "./components/AddUserToGroupModal";

import styles from "./AdminSearchPage.module.scss";

import type { ColumnType } from "antd/lib/table";
import type {
  PermissionGroupPage_group$data,
  PermissionGroupPage_group$key,
} from "__generated__/PermissionGroupPage_group.graphql";
import type { PermissionGroupPageQuery } from "__generated__/PermissionGroupPageQuery.graphql";
import type { PermissionGroupPageRemoveUserMutation } from "__generated__/PermissionGroupPageRemoveUserMutation.graphql";
import type { ConnectionNodeType } from "components/common/Pagination";

const usersFragment = graphql`
  fragment PermissionGroupPage_group on Group
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 10 }
    searchTerm: { type: "String" }
  )
  @refetchable(queryName: "PermissionGroupPageRefetchQuery") {
    id
    users(after: $cursor, first: $count, searchTerm: $searchTerm)
      @connection(key: "PermissionGroupPage_users") {
      __id
      totalCount
      edges {
        node {
          id
          uuid
          email
          displayName
          organization {
            id
            uuid
            name
          }
        }
      }
    }
  }
`;

const rootQuery = graphql`
  query PermissionGroupPageQuery($id: ID!, $searchTerm: String, $cursor: String, $count: Int!) {
    node(id: $id) {
      id
      __typename
      ... on Group {
        name
        description
        ...PermissionGroupPage_group
          @arguments(cursor: $cursor, count: $count, searchTerm: $searchTerm)
      }
    }
  }
`;

export type User = ConnectionNodeType<PermissionGroupPage_group$data["users"]>;

export interface TableProps {
  groupKey: PermissionGroupPage_group$key;
}

const PAGE_SIZE = 30;

const PermissionGroupTable = ({ groupKey }: TableProps) => {
  const paginationQuery = usePaginationFragment<
    PermissionGroupPageQuery,
    PermissionGroupPage_group$key
  >(usersFragment, groupKey);

  const [paginationProps, currentPageEdges, resetPage] = useRelayPagination(
    paginationQuery,
    PAGE_SIZE,
    paginationQuery.data?.users
  );

  const { refetch } = paginationQuery;
  const [isPending, startTransition] = useTransition();
  const [value, handleInput] = useDebouncedState("", searchTerm => {
    startTransition(() => {
      resetPage();
      refetch({ count: PAGE_SIZE, searchTerm });
    });
  });

  const currentPageNodes = currentPageEdges.map(edge => edge.node!);

  const [removeUser] = useMutation<PermissionGroupPageRemoveUserMutation>(graphql`
    mutation PermissionGroupPageRemoveUserMutation(
      $userUuid: String!
      $groupId: ID!
      $connections: [ID!]!
    ) {
      setUserGroupEnabled(userUuid: $userUuid, groupId: $groupId, enabled: false) {
        errors
        user {
          id @deleteEdge(connections: $connections)
        }
      }
    }
  `);

  const getOnConfirm = useCallback(
    (user: User) => () =>
      removeUser({
        variables: {
          userUuid: user.uuid,
          groupId: paginationQuery.data?.id || "",
          connections: [paginationQuery.data?.users?.__id || ""],
        },
      }),
    [removeUser, paginationQuery.data]
  );

  const columns: ColumnType<User>[] = useMemo(
    () => [
      {
        title: "Email",
        dataIndex: "email",
      },
      {
        title: "Organization",
        dataIndex: ["organization"],
        render: (organization?: { name: string; uuid: string }) =>
          organization ? (
            <a href={`/o/${organization.uuid}/fleet`} target="_blank" rel="noreferrer">
              {organization?.name}
            </a>
          ) : (
            "No Organization"
          ),
      },
      {
        title: "Actions",
        render: (user: User) => (
          <Popconfirm
            title="Remove user?"
            onConfirm={getOnConfirm(user)}
            okText="Yes"
            cancelText="No"
          >
            <Button destructive>Remove</Button>
          </Popconfirm>
        ),
      },
    ],
    [getOnConfirm]
  );
  const filters = useMemo(
    () => (
      <Input
        placeholder="Search"
        className={styles.search}
        value={value}
        onValueChange={handleInput}
        isClearable
      />
    ),
    [handleInput, value]
  );

  return (
    <>
      <PaginatedTable
        paginationProps={legacyToRivetPagination(paginationProps, resetPage)}
        columns={columns}
        dataSource={currentPageNodes}
        loading={isPending}
        filters={filters}
      />
    </>
  );
};

const PermissionGroupPage = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const [addUserToGroupModelVisible, setAddUserToGroupModelVisible] = useState(false);
  const query = useLazyLoadQuery<PermissionGroupPageQuery>(rootQuery, {
    id: groupId,
    count: PAGE_SIZE,
  });

  // Get the inner fragment out to extract the __id connection for appending
  const paginationQuery = usePaginationFragment<
    PermissionGroupPageQuery,
    PermissionGroupPage_group$key
  >(usersFragment, query.node);

  const permissionGroupPath = useOrgPrefixedPath("/permission_groups");

  const breadcrumbsPath = useMemo(
    () => [{ name: "Skydio Admin" }, { name: "Permission Groups", linkTo: permissionGroupPath }],
    [permissionGroupPath]
  );

  const breadcrumbs = useMemo(() => <Breadcrumbs path={breadcrumbsPath} />, [breadcrumbsPath]);

  const handleOpenModal = useCallback(() => {
    setAddUserToGroupModelVisible(true);
  }, []);

  const addUserButton = useMemo(
    () => (
      <Button variant="primary" onClick={handleOpenModal}>
        Add User
      </Button>
    ),
    [handleOpenModal]
  );

  if (query.node?.__typename !== "Group") {
    throw new Error(`Invalid node type ${query.node?.__typename}`);
  }

  return (
    <>
      <SettingsPageView
        title={query.node.name || "Permission Group"}
        subTitle={query.node.description || "Manage skydio admins in this permission group"}
        extra={addUserButton}
        breadcrumbs={breadcrumbs}
      >
        <PermissionGroupTable groupKey={query.node} />
        <AddUserToGroupModal
          modalOpen={addUserToGroupModelVisible}
          setModalOpen={setAddUserToGroupModelVisible}
          groupId={groupId}
          connection={paginationQuery.data?.users?.__id}
        />
      </SettingsPageView>
    </>
  );
};

export default PermissionGroupPage;
